import { LandingPageContent } from '../types';

/**
 * Age long-tail page, sibling of /gifts/gifts-for-3-year-olds. Targets the
 * broad "gifts for 4 year olds" query; the existing blog post targets the
 * narrower "personalized gifts for 4 year olds" listicle query — the two
 * cross-link but keep distinct titles.
 */
const giftsForFourYearOlds: LandingPageContent = {
	path: '/gifts/gifts-for-4-year-olds',
	seoTitle: 'Gifts for 4-Year-Olds — Ideas for the Age of Why',
	metaDescription:
		'Gift ideas for 4-year-olds: personalized storybooks, coloring books and keepsakes. What holds attention at four, what falls flat, and when to order.',
	heading: 'Gifts for 4-Year-Olds',
	subheading: 'Old enough for a real story. Young enough to believe every word.',
	heroImage: '/assets/hero/kid-2/watercolor_scene.png',
	breadcrumbParents: [{ name: 'Gifts for kids', url: '/gifts-for-kids' }],
	intro: `Four is the age of *why*. The child has full sentences, firm opinions, an endless appetite for questions, and — usefully for gift-givers — a newly stable sense of who they are.

That self-awareness is the thing to aim at. A four-year-old doesn't just recognise themselves in a story the way a toddler does; they *narrate* themselves. Hand them a book where they are the hero and they will explain to you, at length, exactly what they did on every page and why.`,
	sections: [
		{
			heading: 'What works at four',
			body: `**Personalized storybooks, now with actual plot.** At three, recognition was the whole show. At four, the story matters too — a beginning, a problem, a brave bit, an ending. Twelve to sixteen illustrated pages is right; the child will memorise all of them and correct you when you skip.

**Personalized coloring books.** Motor control has arrived properly. Staying inside the lines is now a point of pride, and pages starring themselves get finished rather than abandoned.

**Pretend-play props.** Anything that supports the running theatrical production that is a four-year-old's day. A t-shirt with their illustrated hero-self feeds directly into it.

**Their name, everywhere.** Most four-year-olds are learning to spot and write their own name. A book with it printed throughout doubles as reading practice they volunteer for.`,
		},
		{
			heading: 'What falls flat at four',
			body: `- **Toys pitched at the box's age range, not the child.** "4+" on the packaging often means "fine for a four-year-old to be near".
- **Long chapter books.** A year or three early. Attention is real now but still measured in minutes, not chapters.
- **Anything requiring turn-taking to be fun.** Board games are borderline at four — some children manage, many combust.
- **Duplicate interests.** The fourth dinosaur set lands with a quarter of the joy of the first. Personalization sidesteps this: there is only ever one book about *this* child.`,
		},
		{
			heading: 'The reveal is the gift',
			body: `Something specific happens when a four-year-old opens a personalized book: a second of silence, the double-take at the cover, and then the announcement — *"That's ME!"* — delivered to everyone in the room, twice.

Four is arguably the peak age for that moment. Younger children enjoy it but can't articulate it; older children feel it but play it cooler. A four-year-old has neither restraint nor doubt, which is exactly what you want on camera at a birthday.`,
		},
		{
			heading: 'Art styles at this age',
			body: `Four-year-olds handle more visual detail than toddlers, so the field is open: **flat modern** and **Pixar-like 3D** remain the crowd-pleasers, but **classic storybook** illustration starts working now too — four is when children begin sitting still for pictures with things *in the background*.

**Watercolour** remains the parents' favourite and the keepsake choice. If the book is meant to survive to adulthood, it's the style that ages best on a shelf.`,
		},
	],
	picks: [
		{
			label: 'Personalized storybooks',
			href: '/personalized-story-books',
			description: 'A real plot, starring them. The core gift at four.',
		},
		{
			label: 'Personalized coloring books',
			href: '/personalized-coloring-books',
			description: 'Pages they finish with pride, now mostly inside the lines.',
		},
		{
			label: 'Custom kids t-shirts',
			href: '/custom-kids-t-shirts',
			description: 'Their hero-self, wearable for pretend play.',
		},
	],
	faqs: [
		{
			question: 'What is a good gift for a 4-year-old?',
			answer:
				'Four-year-olds respond strongly to gifts about themselves: a personalized storybook with a real plot, a coloring book starring them, or a t-shirt with their illustrated character. Stories of twelve to sixteen illustrated pages suit the attention span at this age.',
		},
		{
			question: 'Are personalized books still worth it at four, or is that just for toddlers?',
			answer:
				'Four is arguably the peak age. Unlike a toddler, a four-year-old follows the plot, retells it, and announces the reveal to the whole room — recognition plus narrative is exactly what this age runs on.',
		},
		{
			question: 'What should I avoid buying a 4-year-old?',
			answer:
				'Long chapter books (too early), games that depend on patient turn-taking, and duplicates of an interest they already have plenty of. A gift about the child themselves cannot be a duplicate by definition.',
		},
	],
	related: [
		{ label: 'Gifts for kids', href: '/gifts-for-kids' },
		{ label: 'Gifts for 3-year-olds', href: '/gifts/gifts-for-3-year-olds' },
		{ label: 'Gifts for 5-year-olds', href: '/gifts/gifts-for-5-year-olds' },
		{
			label: '10 personalized gifts for a 4-year-old',
			href: '/blog/personalized-gifts-for-4-year-olds',
		},
	],
	cta: { label: 'Browse gifts', href: '/products' },
};

export default giftsForFourYearOlds;
